import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Chip, CircularProgress, Grid, Switch, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { useCollectionClient } from '../hooks/useCollectionClient';
import { useCollectionClientNotes } from '../hooks/useCollectionClientNotes';
import { useUpdateCollectionClient } from '../hooks/useUpdateCollectionClient';
import { CollectionInvoiceDocument, CollectionInvoicePdf } from '../model';
import { hasMultiDepartmentInvoice } from '../utils/groupInvoices';
import ClientInvoicesTable from '../components/ClientInvoicesTable';
import ClientEmailHistoryTable from '../components/ClientEmailHistoryTable';
import CollectionClientNotesModal from '../components/CollectionClientNotesModal';
import { useToggleCollectionClientContactEmail } from '../hooks/useToggleCollectionClientContactEmail';
import { SectionLabel } from '../components/clientDetailPresentation';
import ClientCompanyInfoCard from '../components/ClientCompanyInfoCard';
import ClientRecentNotesCard from '../components/ClientRecentNotesCard';

const CollectionClientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const clientId = Number(id);
  const history = useHistory();
  const queryClient = useQueryClient();
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);

  const { data, isLoading, isError } = useCollectionClient(clientId);
  const client = data?.data ?? null;
  const { data: notesData } = useCollectionClientNotes(clientId);
  const notes = notesData?.data ?? [];
  const { updateClient, isLoading: isToggling } = useUpdateCollectionClient();
  const { toggleContactEmail, isLoading: isTogglingContactEmail } = useToggleCollectionClientContactEmail();

  const [collectionEnabled, setCollectionEnabled] = useState(false);
  const [documents, setDocuments] = useState<CollectionInvoiceDocument[]>([]);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    if (client) {
      setCollectionEnabled(client.collection_enabled);
      setDocuments(client.invoice_documents);
    }
  }, [client]);

  const handleToggleCollectionEnabled = async (checked: boolean) => {
    if (!client) return;
    const previous = collectionEnabled;
    setCollectionEnabled(checked);
    try {
      await updateClient({ id: clientId, collectionEnabled: checked });
      showSuccessSnackBar?.(`Client ${checked ? 'enabled' : 'disabled'} for collection emails.`);
    } catch (error: any) {
      setCollectionEnabled(previous);
      const serverMessage = error?.response?.data?.errors?.[0] || error?.response?.data?.error;
      showErrorSnackBar?.(serverMessage || 'Failed to update client status.');
    }
  };

  const handleUploaded = (applyTo: string, pdf: CollectionInvoicePdf) => {
    setDocuments((prev) => prev.map((d) => (d.apply_to === applyTo ? { ...d, has_pdf: true, pdf } : d)));
    showSuccessSnackBar?.('Invoice PDF uploaded.');
  };

  const handleDeleted = (applyTo: string) => {
    setDocuments((prev) => prev.map((d) => (d.apply_to === applyTo ? { ...d, has_pdf: false, pdf: null } : d)));
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !client) {
    return (
      <Grid container>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Alert severity="error">Failed to load client.</Alert>
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/collections/clients')}>
            Back to Clients
          </Button>
        </Grid>
      </Grid>
    );
  }

  const notEmailable = !client.emailable;
  const recipientEmails = client.emails ?? [];

  const handleToggleContactEmail = async (conIdNo: string, action: 'add' | 'remove') => {
    try {
      await toggleContactEmail({
        id: clientId,
        con_id_no: conIdNo,
        action,
        location: client.location,
      });
      showSuccessSnackBar?.(
        action === 'add' ? 'Contact added to collection emails.' : 'Contact removed from collection emails.',
      );
    } catch (error: unknown) {
      const err = error as { response?: { data?: { errors?: string[]; error?: string } } };
      const message =
        err?.response?.data?.errors?.[0] || err?.response?.data?.error || 'Failed to update collection emails.';
      showErrorSnackBar?.(message);
      throw error;
    }
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle={`Collections — ${client.company}`} titleIcon={<BusinessIcon />} />
      </Grid>

      <Grid
        item
        xs={12}
        sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}
      >
        <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => history.push('/collections/clients')}>
          Back to Clients
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {client.tone && <Chip label={client.tone} size="small" />}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2">Collection Emails</Typography>
            <Switch
              checked={notEmailable ? false : collectionEnabled}
              onChange={(e) => handleToggleCollectionEnabled(e.target.checked)}
              color="primary"
              size="small"
              disabled={isToggling || notEmailable}
              inputProps={{ 'aria-label': `Collection emails for ${client.company}` }}
            />
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} sx={{ mt: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '65fr 35fr' },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          <ClientCompanyInfoCard
            client={client}
            clientId={clientId}
            invoiceDocuments={documents}
            recipientEmails={recipientEmails}
            contactsOverride={client.contacts.filter((c) => !/statement/i.test(c.name ?? ''))}
            onToggleContactEmail={handleToggleContactEmail}
            isTogglingContactEmail={isTogglingContactEmail}
            onEmailSent={() => {
              void queryClient.invalidateQueries({ queryKey: [`/collections/clients/${clientId}/notes`, undefined] });
              void queryClient.invalidateQueries({
                predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === '/collections/email_sends',
              });
            }}
            onBackupSaved={() => {
              void queryClient.invalidateQueries({ queryKey: [`/collections/clients/${clientId}`, undefined] });
            }}
          />

          <ClientRecentNotesCard notes={notes} onViewAll={() => setNotesOpen(true)} />
        </Box>
      </Grid>

      <Grid item xs={12} sx={{ mt: 2.5 }}>
        <SectionLabel>Aging Invoices</SectionLabel>
        {hasMultiDepartmentInvoice(documents) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Invoices spanning multiple departments are listed under each. Department totals may exceed the client&apos;s
            total past due.
          </Typography>
        )}
        <ClientInvoicesTable
          documents={documents}
          location={client.location}
          acctNo={client.acct_no}
          onUploaded={handleUploaded}
          onDeleted={handleDeleted}
        />
      </Grid>

      <Grid item xs={12} sx={{ mt: 2.5 }}>
        <SectionLabel>Email History</SectionLabel>
        <ClientEmailHistoryTable location={client.location} clientId={clientId} />
      </Grid>

      <CollectionClientNotesModal
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        clientId={clientId}
        company={client.company}
        onNotesChanged={() => {
          void queryClient.invalidateQueries({ queryKey: [`/collections/clients/${clientId}/notes`, undefined] });
        }}
      />
    </Grid>
  );
};

export default CollectionClientDetailPage;
